import { toast } from "react-toastify";
import { db } from "../../firebase"; // Adjust as needed
import { runTransaction } from "firebase/firestore";

/**
 * Autofills courses for all semesters using the new schedule structure.
 *
 * For each year and friendly term (semester), this function:
 *   - Initializes missing schedule entries using fetchedTerms.
 *   - Gathers courses from various categories (major, electives, requirements, etc.)
 *   - Updates the local state immediately and then updates Firestore in one bulk transaction.
 *
 * @param {Object} params
 * @param {Object} params.currentUser - The logged-in user.
 * @param {string} params.effectiveUserId - The user ID for the plan being edited.
 * @param {string} params.activeTab - The active plan/tab.
 * @param {function} params.recheckPrerequisites - Function to re-check prerequisites.
 * @param {function} params.setFinalizedOrganizedCourses - Function to update local state.
 * @param {Object[]} params.allCourses - Array of all course objects.
 * @param {Object} params.majorCourses - Data for major courses.
 * @param {function} params.organizeMajorCoursesByYearAndSemester - Helper to organize major courses.
 * @param {Object} params.majorElectives - Data for major electives.
 * @param {Object} params.majorRequirementCourses - Data for major requirements.
 * @param {Object} params.minorCourses - Data for minor courses.
 * @param {Object} params.minorRequirementCourses - Data for minor requirements.
 * @param {Object} params.finalizedOrganizedCourses - Current local state of organized courses.
 * @param {number} params.maxCreditsPerSemester - Maximum allowed credits per semester.
 * @param {number[]} params.years - Array of year numbers (e.g. [1,2,3,4]).
 * @param {string[]} params.semesters - Array of friendly semester names (e.g. ["Fall", "Spring"]).
 * @param {Object} params.completedSemesters - Object flagging completed semesters (keys use friendly term names).
 * @param {Object} params.fetchedTerms - Mapping of term keys to friendly term names for initialization.
 */
export async function autofillAllCourses({
  currentUser,
  effectiveUserId,
  activeTab,
  recheckPrerequisites,
  setFinalizedOrganizedCourses,
  allCourses,
  majorCourses,
  organizeMajorCoursesByYearAndSemester,
  majorElectives,
  majorRequirementCourses,
  minorCourses,
  minorRequirementCourses,
  finalizedOrganizedCourses,
  maxCreditsPerSemester,
  years,
  semesters,
  completedSemesters,
  fetchedTerms,
}) {
  const toastId = toast.loading("Calculating all autofill courses...", {
    position: "top-center",
  });

  if (!currentUser || !effectiveUserId) {
    console.error("Error: currentUser or effectiveUserId is not defined");
    toast.update(toastId, {
      render: "User data missing!",
      type: "error",
      isLoading: false,
      autoClose: 3000,
    });
    return;
  }

  // Get the current user's document.
  const userRef = db.collection("users").doc(effectiveUserId);
  let userDoc;
  try {
    userDoc = await userRef.get();
  } catch (error) {
    console.error("Error fetching user document:", error);
    toast.update(toastId, {
      render: "Error fetching user data",
      type: "error",
      isLoading: false,
      autoClose: 3000,
    });
    return;
  }
  if (!userDoc.exists) {
    console.error("User data not found");
    toast.update(toastId, {
      render: "User data not found",
      type: "error",
      isLoading: false,
      autoClose: 3000,
    });
    return;
  }
  const userData = userDoc.data();

  // Use the new schedule structure:
  // planData will use keys like "year1", and within each year the term key will be the friendly name.
  const planData = { ...(userData.schedules?.[activeTab] || {}) };
  let updatedPlanData = { ...planData };
  let updatedFinalizedOrganizedCourses = { ...finalizedOrganizedCourses };

  // Loop over each year and semester.
  for (const yearNumber of years) {
    const yearKey = `year${yearNumber}`;

    // Initialize the year in planData using fetchedTerms if missing.
    if (!updatedPlanData[yearKey]) {
      updatedPlanData[yearKey] = {};
      // Initialize every term from fetchedTerms with default structure.
      Object.keys(fetchedTerms).forEach((tk) => {
        const termFriendly = fetchedTerms[tk];
        updatedPlanData[yearKey][termFriendly] = { courses: [], Placeholders: [] };
      });
    }
    // Similarly, initialize the local state for the year.
    if (!updatedFinalizedOrganizedCourses[yearKey]) {
      updatedFinalizedOrganizedCourses[yearKey] = {};
      Object.keys(fetchedTerms).forEach((tk) => {
        const termFriendly = fetchedTerms[tk];
        updatedFinalizedOrganizedCourses[yearKey][termFriendly] = [];
      });
    }

    // Loop over each friendly semester name.
    for (const semester of semesters) {
      // Skip if this semester is marked as completed.
      if (
        completedSemesters &&
        completedSemesters[yearKey] &&
        completedSemesters[yearKey][semester] === true
      ) {
        continue;
      }

      // Ensure the schedule for this term exists.
      if (!updatedPlanData[yearKey][semester]) {
        updatedPlanData[yearKey][semester] = { courses: [], Placeholders: [] };
      }
      if (!updatedFinalizedOrganizedCourses[yearKey][semester]) {
        updatedFinalizedOrganizedCourses[yearKey][semester] = [];
      }
      const existingFinalizedSchedule = updatedPlanData[yearKey][semester].courses || [];

      // Collect all course numbers already in the plan.
      const allCourseNumbersInPlan = [];
      Object.values(updatedPlanData).forEach((yearData) => {
        Object.values(yearData).forEach((termData) => {
          if (termData?.courses) {
            allCourseNumbersInPlan.push(...termData.courses);
          }
        });
      });

      // Gather course numbers already in the local finalized state.
      const allFinalizedCourses = new Set();
      Object.values(updatedFinalizedOrganizedCourses).forEach((yearData) => {
        Object.values(yearData).forEach((termCourses) => {
          termCourses.forEach((course) => {
            allFinalizedCourses.add(course.courseInfo_courseNumber);
          });
        });
      });

      // Filter eligible courses.
      const allAutofillableCourses = allCourses.filter(
        (course) =>
          course.courseYear <= 4 &&
          course.Offering.includes(semester) &&
          !allFinalizedCourses.has(course.courseInfo_courseNumber) &&
          course.isPrereqMet &&
          course.Credits < 6
      );
      

      const electiveCoursesEligible = allCourses.filter(
        (course) =>
          course.courseYear <= yearNumber && // only courses up to the current year
          course.Offering.includes(semester) &&
          !allFinalizedCourses.has(course.courseInfo_courseNumber) &&
          course.isPrereqMet &&
          course.Credits < 6
      );

      // ===============================
      // 1) MAJOR COURSES
      // ===============================
      const combinedSemesterMajorCourses = [];
      Object.entries(majorCourses).forEach(([, courseInfo]) => {
        const byYearAndSem = organizeMajorCoursesByYearAndSemester(courseInfo);
        if (byYearAndSem && byYearAndSem[yearNumber]) {
          combinedSemesterMajorCourses.push(...(byYearAndSem[yearNumber][semester] || []));
        }
      });
      const semesterMajorCoursesWithAutofill = combinedSemesterMajorCourses.map((c) => ({
        ...c,
        autofillType: "Major Course",
      }));

      // ===============================
      // 2) MAJOR ELECTIVES
      // ===============================
      const combinedElectiveCourses = [];
      Object.entries(majorElectives).forEach(([, electivesByMajor]) => {
        if (electivesByMajor?.[`year${yearNumber}`]) {
          combinedElectiveCourses.push(...(electivesByMajor[`year${yearNumber}`][semester] || []));
        }
      });
      const electiveNameStrings = combinedElectiveCourses.map((course) => course.Possibilities);
      const semesterElectiveRequirements = electiveNameStrings.reduce((acc, name) => {
        if (!acc[name]) {
          const course = combinedElectiveCourses.find((c) => c.Possibilities === name);
          const electiveFullName = course ? course.Requirements : "";
          acc[name] = { quantity: 0, electiveFullName };
        }
        acc[name].quantity += 1;
        return acc;
      }, {});
      let autofillElectiveCourses = [];
      Object.entries(semesterElectiveRequirements).forEach(
        ([requirementName, { quantity, electiveFullName }]) => {
          let existingCoursesInSemester = [];
          if (requirementName === "Free-NonMajor-Elective") {
            existingCoursesInSemester = existingFinalizedSchedule
              .map((num) => allCourses.find((c) => c.courseInfo_courseNumber === num))
              .filter(Boolean);
          } else {
            existingCoursesInSemester = existingFinalizedSchedule
              .map((num) => allCourses.find((c) => c.courseInfo_courseNumber === num))
              .filter((course) =>
                course?.Elective_fulfillment.includes(requirementName)
              );
          }
          const remainingQuantity = quantity - existingCoursesInSemester.length;
          if (remainingQuantity > 0) {
            let filteredCourses = [];
            if (requirementName === "Free-NonMajor-Elective") {
              filteredCourses = electiveCoursesEligible.filter((course) => {
                const isDuplicate = allCourseNumbersInPlan.includes(
                  course.courseInfo_courseNumber
                );
                return !isDuplicate && course.Credits >= 4;
              });
            } else {
              filteredCourses = electiveCoursesEligible.filter((course) => {
                const isDuplicate = allCourseNumbersInPlan.includes(
                  course.courseInfo_courseNumber
                );
                return (
                  course.Elective_fulfillment.includes(requirementName) &&
                  !isDuplicate
                );
              });
            }
            const sortedCourses = filteredCourses.sort((a, b) => b.recScore - a.recScore);
            const topCourses = sortedCourses
              .slice(0, remainingQuantity)
              .map((c) => ({
                ...c,
                electiveRequirement: requirementName,
                electiveFullName,
              }));
            autofillElectiveCourses.push(...topCourses);
          }
        }
      );
      const autofillElectiveCoursesWithAutofill = autofillElectiveCourses.map(
        (course) => ({
          ...course,
          autofillType: "Elective",
        })
      );

      // ===============================
      // 3) MAJOR REQUIREMENTS
      // ===============================
      const combinedRequirementCourses = [];
      Object.entries(majorRequirementCourses).forEach(([, requirementsByMajor]) => {
        if (requirementsByMajor?.[`year${yearNumber}`]) {
          combinedRequirementCourses.push(...(requirementsByMajor[`year${yearNumber}`][semester] || []));
        }
      });
      const requirementNameStrings = combinedRequirementCourses.map((c) => c.Requirements);
      const semesterRequirementDetails = requirementNameStrings.reduce((acc, name) => {
        if (!acc[name]) {
          const course = combinedRequirementCourses.find((req) => req.Requirements === name);
          const possibleCourses = course
            ? course.Possibilities.split(",").map((x) => x.trim())
            : [];
          acc[name] = { quantity: 0, possibleCourses };
        }
        acc[name].quantity += 1;
        return acc;
      }, {});
      let autofillRequirementCourses = [];
      Object.entries(semesterRequirementDetails).forEach(
        ([requirementName, { quantity, possibleCourses }]) => {
          const alreadyUsedCount = possibleCourses.filter((num) =>
            existingFinalizedSchedule.includes(num)
          ).length;
          const remainingQuantity = quantity - alreadyUsedCount;
          if (remainingQuantity > 0) {
            const filtered = possibleCourses
              .map((courseNumber) =>
                allAutofillableCourses.find(
                  (c) =>
                    c.courseInfo_courseNumber === courseNumber &&
                    !allCourseNumbersInPlan.includes(courseNumber) &&
                    c.courseYear <= yearNumber
                )
              )
              .filter(Boolean);
            const sortedCourses = filtered.sort((a, b) => b.recScore - a.recScore);
            const top = sortedCourses.slice(0, remainingQuantity).map((c) => ({
              ...c,
              requirementName,
            }));
            autofillRequirementCourses.push(...top);
          }
        }
      );
      const autofillRequirementCoursesWithAutofill = autofillRequirementCourses.map(
        (c) => ({
          ...c,
          autofillType: "Requirement",
        })
      );

      // ===============================
      // 4) MINOR COURSES
      // ===============================
      const combinedMinorCourses = [];
      Object.entries(minorCourses).forEach(([, requirementsByMinor]) => {
        if (
          requirementsByMinor?.[`year${yearNumber}`] &&
          requirementsByMinor[`year${yearNumber}`][semester]
        ) {
          combinedMinorCourses.push(...requirementsByMinor[`year${yearNumber}`][semester]);
        }
      });
      let autofillMinorCoursesWithAutofill = [];
      if (combinedMinorCourses.length) {
        const minorNums = combinedMinorCourses.map((c) => c.courseInfo_courseNumber);
        const autofillMinorCoursesUnique = minorNums
          .map((num) =>
            allAutofillableCourses.find(
              (c) =>
                c.courseInfo_courseNumber === num &&
                !allCourseNumbersInPlan.includes(num)
            )
          )
          .filter(Boolean);
        autofillMinorCoursesWithAutofill = autofillMinorCoursesUnique.map((c) => ({
          ...c,
          autofillType: "Minor Course",
        }));
      }

      // ===============================
      // 5) MINOR REQUIREMENTS
      // ===============================
      const combinedMinorReqCourses = [];
      Object.entries(minorRequirementCourses).forEach(([, reqsByMinor]) => {
        if (
          reqsByMinor?.[`year${yearNumber}`] &&
          reqsByMinor[`year${yearNumber}`][semester]
        ) {
          combinedMinorReqCourses.push(...reqsByMinor[`year${yearNumber}`][semester]);
        }
      });
      let autofillMinorRequirementCourses = [];
      if (combinedMinorReqCourses.length) {
        const requirementMinorNameStrings = combinedMinorReqCourses.map((c) => c.Requirements);
        const semesterMinorReqDetails = requirementMinorNameStrings.reduce((acc, name) => {
          if (!acc[name]) {
            const c = combinedMinorReqCourses.find((x) => x.Requirements === name);
            const possible = c ? c.Possibilities.split(",").map((x) => x.trim()) : [];
            acc[name] = { quantity: 0, possibleCourses: possible };
          }
          acc[name].quantity += 1;
          return acc;
        }, {});
        Object.entries(semesterMinorReqDetails).forEach(
          ([reqName, { quantity, possibleCourses }]) => {
            const hasCount = possibleCourses.filter((num) =>
              existingFinalizedSchedule.includes(num)
            ).length;
            const needed = quantity - hasCount;
            if (needed > 0) {
              const filtered = possibleCourses
                .map((num) =>
                  allAutofillableCourses.find(
                    (c) =>
                      c.courseInfo_courseNumber === num &&
                      !allCourseNumbersInPlan.includes(num)
                  )
                )
                .filter(Boolean);
              const sorted = filtered.sort((a, b) => b.recScore - a.recScore);
              const top = sorted.slice(0, needed).map((x) => ({
                ...x,
                requirementName: reqName,
              }));
              autofillMinorRequirementCourses.push(...top);
            }
          }
        );
      }
      const autofillMinorRequirementCoursesWithAutofill = autofillMinorRequirementCourses.map(
        (c) => ({
          ...c,
          autofillType: "Requirement",
        })
      );

      // ===============================
      // 6) COMBINE ALL AUTOFILL COURSES
      // ===============================
      const allSemesterAutofillCourses = [
        ...semesterMajorCoursesWithAutofill,
        ...autofillElectiveCoursesWithAutofill,
        ...autofillRequirementCoursesWithAutofill,
        ...autofillMinorCoursesWithAutofill,
        ...autofillMinorRequirementCoursesWithAutofill,
      ];

      // Remove duplicates (by courseInfo_courseNumber).
      const uniqueAutofillCourses = allSemesterAutofillCourses.reduce((acc, curr) => {
        const exists = acc.find(
          (item) => item.courseInfo_courseNumber === curr.courseInfo_courseNumber
        );
        return exists ? acc : [...acc, curr];
      }, []);

      // Exclude any courses already in the plan.
      const trulyUnique = uniqueAutofillCourses.filter(
        (c) => !allCourseNumbersInPlan.includes(c.courseInfo_courseNumber)
      );

      // Check current credit count for this semester.
      const existingFinalizedScheduleObjects = allCourses.filter((c) =>
        existingFinalizedSchedule.includes(c.courseInfo_courseNumber)
      );
      const existingCreditCount = existingFinalizedScheduleObjects.reduce(
        (total, c) => total + c.Credits,
        0
      );
      const maxAdditionalCredits = maxCreditsPerSemester - existingCreditCount;
      let accumulated = 0;
      const truncatedAutofillCourses = [];
      for (const course of trulyUnique) {
        if (accumulated + course.Credits > maxAdditionalCredits) break;
        truncatedAutofillCourses.push(course);
        accumulated += course.Credits;
      }
      const newAutofillCourses = truncatedAutofillCourses.map((course) => ({
        ...course,
        isAutofill: true,
      }));

      // Generate updated course IDs for this semester.
      const newCourseIds = [
        ...new Set([
          ...existingFinalizedSchedule,
          ...newAutofillCourses.map((c) => c.courseInfo_courseNumber),
        ]),
      ];
      updatedPlanData[yearKey][semester].courses = newCourseIds;

      // Update local finalized courses for this term.
      const existingNums = updatedFinalizedOrganizedCourses[yearKey][semester].map(
        (c) => c.courseInfo_courseNumber
      );
      const filteredNewCourses = newAutofillCourses.filter(
        (c) => !existingNums.includes(c.courseInfo_courseNumber)
      );
      updatedFinalizedOrganizedCourses[yearKey][semester] = [
        ...updatedFinalizedOrganizedCourses[yearKey][semester],
        ...filteredNewCourses,
      ];

      // Immediately update local state.
      setFinalizedOrganizedCourses(updatedFinalizedOrganizedCourses);
      recheckPrerequisites(updatedFinalizedOrganizedCourses);

      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // ============================================
  // Ensure 45 Upper Division Credits Dynamically
  // ============================================
  let totalUpperDivisionCredits = 0;
  Object.values(updatedFinalizedOrganizedCourses).forEach((yearData) => {
    Object.values(yearData).forEach((termCourses) => {
      const semesterUpperCredits = termCourses.reduce((sum, course) => {
        const match = course.courseInfo_courseNumber.match(/(\d+)/);
        const courseNum = match ? parseInt(match[1], 10) : 100;
        const courseCredits =
          course.Credits === "N/A" || course.Credits === undefined
            ? 0
            : Number(course.Credits);
        return courseNum >= 300 ? sum + courseCredits : sum;
      }, 0);
      totalUpperDivisionCredits += semesterUpperCredits;
    });
  });

  console.log(completedSemesters, 'hihi')


  // Dynamically add upper division "Free-NonMajor-Elective" courses until 45 credits.
  while (totalUpperDivisionCredits < 45) {
    // Recompute all courses in the plan.
    const allCourseNumbersInPlan = [];
    Object.values(updatedPlanData).forEach((yearData) => {
      Object.values(yearData).forEach((termData) => {
        if (termData?.courses) {
          allCourseNumbersInPlan.push(...termData.courses);
        }
      });
    });

    // Choose candidate years – prefer year 3 and above, or else the highest year.
    let candidateYears = years.filter((y) => y >= 3);
    if (candidateYears.length === 0) {
      candidateYears = [Math.max(...years)];
    }

    let candidateYearKey = "";
    let candidateSemester = "";
    let candidateMinCredits = Infinity;
    candidateYears.forEach((year) => {
      const yKey = `year${year}`;
      if (updatedFinalizedOrganizedCourses[yKey]) {
        Object.entries(updatedFinalizedOrganizedCourses[yKey]).forEach(
          ([term, courses]) => {
            if (term.toLowerCase() === "summer") return;
            if (completedSemesters?.[yKey]?.[term] === true) return;
            const semCredits = courses.reduce(
              (sum, course) =>
                sum +
                (course.Credits === "N/A" || course.Credits === undefined
                  ? 0
                  : Number(course.Credits)),
              0
            );
            if (semCredits < candidateMinCredits) {
              candidateMinCredits = semCredits;
              candidateYearKey = yKey;
              candidateSemester = term; // friendly term (e.g. "Fall")
            }
          }
        );
      }
    });

    if (!candidateYearKey || !candidateSemester) break;

    // Filter eligible upper division courses.
    const eligibleUpperDivisionCourses = allCourses.filter((course) => {
      const match = course.courseInfo_courseNumber.match(/(\d+)/);
      const courseNum = match ? parseInt(match[1], 10) : 100;
      const isDuplicate = allCourseNumbersInPlan.includes(course.courseInfo_courseNumber);
      return (
        course.courseYear <= 4 &&
        course.Offering.includes(candidateSemester) &&
        courseNum >= 300 &&
        !isDuplicate &&
        course.isPrereqMet &&
        course.Credits >= 3 &&
        course.Credits < 6
      );
    });
    const sortedEligible = eligibleUpperDivisionCourses.sort((a, b) => b.recScore - a.recScore);
    const selectedCourse = sortedEligible[0];
    if (!selectedCourse) break;

    // Add the selected course.
    updatedFinalizedOrganizedCourses[candidateYearKey][candidateSemester].push({
      ...selectedCourse,
      autofillType: "Elective",
      isAutofill: true,
    });
    // Update the plan data.
    if (updatedPlanData[candidateYearKey] && updatedPlanData[candidateYearKey][candidateSemester]) {
      updatedPlanData[candidateYearKey][candidateSemester].courses.push(
        selectedCourse.courseInfo_courseNumber
      );
    } else {
      if (!updatedPlanData[candidateYearKey]) {
        updatedPlanData[candidateYearKey] = {};
      }
      updatedPlanData[candidateYearKey][candidateSemester] = {
        courses: [selectedCourse.courseInfo_courseNumber],
        Placeholders: [],
      };
    }

    setFinalizedOrganizedCourses(updatedFinalizedOrganizedCourses);
    recheckPrerequisites(updatedFinalizedOrganizedCourses);
    await new Promise((resolve) => setTimeout(resolve, 100));
    totalUpperDivisionCredits += Number(selectedCourse.Credits);
  }

  // ============================================
  // Ensure total credits reach 120
  // ============================================
  let totalCredits = 0;
  Object.values(updatedFinalizedOrganizedCourses).forEach((yearData) => {
    Object.values(yearData).forEach((termCourses) => {
      const semesterCredits = termCourses.reduce((sum, course) => {
        const courseCredits =
          course.Credits === "N/A" || course.Credits === undefined
            ? 0
            : Number(course.Credits);
        return sum + courseCredits;
      }, 0);
      totalCredits += semesterCredits;
    });
  });

  while (totalCredits < 120) {
    const allCourseNumbersInPlan = [];
    Object.values(updatedPlanData).forEach((yearData) => {
      Object.values(yearData).forEach((termData) => {
        if (termData?.courses) {
          allCourseNumbersInPlan.push(...termData.courses);
        }
      });
    });

    // Consider all available years.
    const candidateYears = [...years];
    let candidateYearKey = "";
    let candidateSemester = "";
    let candidateMinCredits = Infinity;
    candidateYears.forEach((year) => {
      const yKey = `year${year}`;
      if (updatedFinalizedOrganizedCourses[yKey]) {
        Object.entries(updatedFinalizedOrganizedCourses[yKey]).forEach(
          ([term, courses]) => {
            if (term.toLowerCase() === "summer") return;
            if (completedSemesters?.[yKey]?.[term] === true) return;
            const semCredits = courses.reduce(
              (sum, course) =>
                sum +
                (course.Credits === "N/A" || course.Credits === undefined
                  ? 0
                  : Number(course.Credits)),
              0
            );
            if (semCredits < candidateMinCredits) {
              candidateMinCredits = semCredits;
              candidateYearKey = yKey;
              candidateSemester = term;
            }
          }
        );
      }
    });
    if (!candidateYearKey || !candidateSemester) break;

    const eligibleCourses = allCourses.filter((course) => {
      const isDuplicate = allCourseNumbersInPlan.includes(course.courseInfo_courseNumber);
      return (
        course.courseYear <= Number(candidateYearKey[4]) &&
        course.Offering.includes(candidateSemester) &&
        !isDuplicate &&
        course.isPrereqMet &&
        course.Credits >= 3 &&
        course.Credits < 6
      );
    });
    const sortedEligible = eligibleCourses.sort((a, b) => b.recScore - a.recScore);
    const selectedCourse = sortedEligible[0];
    if (!selectedCourse) break;

    updatedFinalizedOrganizedCourses[candidateYearKey][candidateSemester].push({
      ...selectedCourse,
      autofillType: "Elective",
      isAutofill: true,
    });
    const candidateTermData = updatedPlanData[candidateYearKey][candidateSemester] || { courses: [], Placeholders: [] };
    candidateTermData.courses.push(selectedCourse.courseInfo_courseNumber);
    updatedPlanData[candidateYearKey][candidateSemester] = candidateTermData;

    setFinalizedOrganizedCourses(updatedFinalizedOrganizedCourses);
    recheckPrerequisites(updatedFinalizedOrganizedCourses);
    await new Promise((resolve) => setTimeout(resolve, 100));
    totalCredits += Number(selectedCourse.Credits);
  }

  // Finally, update Firestore with the entire updated schedule.
  try {
    await runTransaction(db, async (transaction) => {
      transaction.update(userRef, {
        [`schedules.${activeTab}`]: updatedPlanData,
      });
    });
    toast.update(toastId, {
      render: "All courses autofilled successfully!",
      type: "success",
      isLoading: false,
      autoClose: 3000,
    });
  } catch (error) {
    console.error("Error updating Firestore:", error);
    toast.update(toastId, {
      render: "Error autofilling courses!",
      type: "error",
      isLoading: false,
      autoClose: 3000,
    });
  }
}
